'use client';

import { useCallback, useEffect, useState } from 'react';

import { logger } from '@/lib/logger';
import { type IShippingLocation, type IShippingProvider, shippingService } from '@/services';

import ShippingLocationSelector from './ShippingLocationSelector';

export type ShippingMethod = {
  id: string;
  name: string;
  code: string;
  description: string;
  cost: number;
  cost_type?: 'FIXED' | 'PERCENTAGE';
  estimatedDays?: string;
  provider?: string;
  type?: string;
  providerId?: string;
  locationsEndpoint?: string;
  freeShippingThreshold?: number;
};

// Currency formatter utility
const toCurrencyFormat = (value: number, currency: string = 'ARS') => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency,
  }).format(Number(value));
};

const getDeliveryOptionName = (provider?: string, type?: 'DELIVERY' | 'TAKEAWAY' | 'STANDARD' | 'EXPRESS'): string => {
  switch (type) {
    case 'TAKEAWAY':
      if (provider === 'OCA') {
        return 'Retiro por OCA';
      }
      return 'Retiro en sucursal de CRAYON';
    case 'DELIVERY':
    case 'STANDARD':
    case 'EXPRESS':
      if (provider === 'OCA') {
        return 'Envío a domicilio por OCA';
      }
      return 'Envío a domicilio';
    default:
      return provider ? `Envío con ${provider}` : 'Envío estándar';
  }
};

const getDeliveryOptionDescription = (provider?: string, type?: 'DELIVERY' | 'TAKEAWAY' | 'STANDARD' | 'EXPRESS'): string => {
  switch (type) {
    case 'TAKEAWAY':
      if (provider === 'OCA') {
        return 'Retirá tu pedido en la sucursal OCA más cercana a tu domicilio';
      }
      return 'Retirá tu pedido directamente en nuestra sucursal más cercana';
    case 'DELIVERY':
    case 'STANDARD':
    case 'EXPRESS':
      if (provider === 'OCA') {
        return 'Recibí tu pedido en la comodidad de tu hogar u oficina con OCA';
      }
      return 'Recibí tu pedido en la comodidad de tu hogar u oficina';
    default:
      return provider ? `Envío con ${provider}` : 'Envío estándar';
  }
};

// Helper to get estimated delivery days
const getEstimatedDelivery = (minDays?: number | null, maxDays?: number | null): string => {
  if (!minDays && !maxDays) {
    return '24hs';
  }
  if (minDays != null && maxDays != null && minDays === maxDays) {
    return `${minDays} días hábiles`;
  }
  if (minDays != null && maxDays != null) {
    return `${minDays}-${maxDays} días hábiles`;
  }
  if (minDays != null || maxDays != null) {
    return `${minDays ?? maxDays} días hábiles`;
  }
  return '24hs'; // Default if no delivery days are specified
};

// Helper to calculate shipping cost
const calculateShippingCost = (provider: IShippingProvider, subtotal: number): number => {
  // Check if free shipping threshold is met
  if (provider.free_shipping_threshold && subtotal >= provider.free_shipping_threshold) {
    return 0;
  }

  // If no fixed cost, it's free shipping
  if (!provider.fixed_cost) {
    return 0;
  }

  // Calculate based on cost type
  if (provider.cost_type === 'PERCENTAGE' && provider.cost_percentage) {
    return subtotal * (provider.cost_percentage / 100);
  }

  return provider.fixed_cost;
};

// Map shipping providers to ShippingMethod
export const mapProviderToShippingMethod = (provider: IShippingProvider, subtotal: number = 0): ShippingMethod => {
  const cost = calculateShippingCost(provider, subtotal);
  const estimatedDays = getEstimatedDelivery(provider.min_delivery_days, provider.max_delivery_days);

  const normalizedProvider = provider.provider ?? undefined;
  const normalizedType = provider.type === 'DELIVERY' || provider.type === 'TAKEAWAY' || provider.type === 'STANDARD' || provider.type === 'EXPRESS' ? provider.type : undefined;

  return {
    id: String(provider.id) || provider.code || `${normalizedProvider}-${provider.type}`,
    code: provider.code || '',
    name: getDeliveryOptionName(normalizedProvider, normalizedType),
    description: getDeliveryOptionDescription(normalizedProvider, normalizedType),
    cost,
    cost_type: (provider.cost_type as 'FIXED' | 'PERCENTAGE') || 'FIXED',
    estimatedDays,
    provider: normalizedProvider,
    type: normalizedType,
    providerId: String(provider.id),
    freeShippingThreshold: provider.free_shipping_threshold ?? undefined,
  };
};

type CartShippingMethodSelectorProps = {
  selectedShipping: ShippingMethod;
  onShippingChange: (method: ShippingMethod) => void;
  subtotal?: number;
  preloadedLocation?: IShippingLocation;
  onLocationSelected?: (location: IShippingLocation) => void;
  /** Pre-fetched shipping methods from a parent component. When provided, the internal fetch is skipped. */
  preloadedMethods?: ShippingMethod[];
};

export const CartShippingMethodSelector: React.FC<CartShippingMethodSelectorProps> = ({
  selectedShipping,
  onShippingChange,
  subtotal = 0,
  preloadedLocation,
  onLocationSelected,
  preloadedMethods,
}) => {
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>(preloadedMethods ?? []);
  const [isLoading, setIsLoading] = useState(!preloadedMethods);

  const applyMethods = useCallback(
    (methods: ShippingMethod[]) => {
      setShippingMethods(methods);
      if (methods.length > 0) {
        const currentMethod = methods.find((m) => m.id === selectedShipping?.id);
        if (!selectedShipping?.id || !currentMethod) {
          onShippingChange(methods[0]);
        } else if (currentMethod.cost !== selectedShipping.cost) {
          onShippingChange(currentMethod);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [subtotal],
  );

  const loadShippingProviders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await shippingService.getShippingProviders();
      const providers: IShippingProvider[] = response && !(response as any).error ? (response as IShippingProvider[]) : [];

      // Filter providers whose status is 'ACTIVE' and that have an identifying code
      const activeMethods = providers
        .filter((p) => p.status === 'ACTIVE' && !!p.code)
        .map((p) => mapProviderToShippingMethod(p, subtotal));

      applyMethods(activeMethods);
      logger.debug({ msg: 'Shipping providers loaded (internal)', count: activeMethods.length });
    } catch (error) {
      logger.error({ msg: 'Error loading shipping providers', error });
      setShippingMethods([]);
    } finally {
      setIsLoading(false);
    }
  }, [subtotal, applyMethods]);

  useEffect(() => {
    if (preloadedMethods) {
      // Sync if the parent refreshes the list (e.g. subtotal changed)
      applyMethods(preloadedMethods);
      setIsLoading(false);
    } else {
      loadShippingProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedMethods]);

  useEffect(() => {
    // When subtotal changes and we own the fetch, reload to recalculate costs
    if (!preloadedMethods) {
      loadShippingProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  if (isLoading) {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Método de envío:</label>
        <div className="p-4 border border-gray-200 rounded-lg text-center text-gray-500">Cargando métodos de envío...</div>
      </div>
    );
  }

  if (shippingMethods.length === 0) {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Método de envío:</label>
        <div className="p-4 border border-gray-200 rounded-lg text-center text-gray-500">No hay métodos de envío disponibles</div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Método de envío:</label>
      <div className="space-y-2">
        {shippingMethods.map((method) => (
          <div key={method.id}>
            <label
              className={`
                                flex items-start p-3 border rounded-lg cursor-pointer transition-all
                                ${selectedShipping.id === method.id ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-300'}
                            `}
            >
              <input
                type="radio"
                name="shipping"
                value={method.id}
                checked={selectedShipping.id === method.id}
                onChange={() => onShippingChange(method)}
                className="mt-1 mr-3 text-primary focus:ring-primary"
              />
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{method.name}</p>
                    <p className="text-sm text-gray-600">{method.description}</p>
                    {Boolean(method.estimatedDays) && <p className="text-xs text-gray-500 mt-1">📦 {method.estimatedDays}</p>}
                    {Boolean(method.freeShippingThreshold) && Boolean(method.cost > 0) && method.freeShippingThreshold && (
                      <p className="text-xs text-primary mt-1">💰 Envío gratis en compras mayores a {toCurrencyFormat(method.freeShippingThreshold)}</p>
                    )}
                  </div>
                  <p className={`font-semibold ${method.cost === 0 ? 'text-success' : 'text-gray-900'}`}>{method.cost === 0 ? 'Gratis' : toCurrencyFormat(method.cost)}</p>
                </div>
              </div>
            </label>

            {/* Show location selector for takeaway methods */}
            {selectedShipping.id === method.id && (method.type === 'TAKEAWAY' || method.type === 'PICKUP') && method.providerId && onLocationSelected && (
              <ShippingLocationSelector
                providerId={method.providerId}
                providerName={method.provider || 'Proveedor'}
                addressType={method.type}
                preloadedLocation={preloadedLocation}
                onLocationSelected={onLocationSelected}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Export SHIPPING_METHODS for backwards compatibility (deprecated)
export const SHIPPING_METHODS: ShippingMethod[] = [];

export default CartShippingMethodSelector;
